import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { listData, updateData, createData, deleteData, fmtMoney } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, CurrencyDollar, Wallet, ChartPieSlice } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

export default function AdBudgetPage() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const key = ["data", "ad_budget", activeId];
  const { data: rows = [] } = useQuery({ queryKey: key, queryFn: () => listData("ad_budget", activeId), enabled: !!activeId });
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null);
  const inv = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); };

  const saveM = useMutation({
    mutationFn: (d) => d.id ? updateData("ad_budget", d.id, d) : createData("ad_budget", { ...d, client_id: activeId }),
    onSuccess: () => { inv(); toast.success("Saved"); },
  });
  const delM = useMutation({ mutationFn: (id) => deleteData("ad_budget", id), onSuccess: () => { inv(); toast.success("Removed"); } });

  const planned = rows.reduce((s, r) => s + Number(r.planned || 0), 0);
  const spent = rows.reduce((s, r) => s + Number(r.spent || 0), 0);

  return (
    <PageContainer>
      <PageHeader eyebrow="Paid Media" title="Advertising Budget Planner"
        subtitle="Allocate your $0–$500/mo across channels. Start with retargeting + Shopping, scale only proven winners.">
        <Button data-testid="add-ad_budget-btn" onClick={() => { setCur({ channel: "", planned: 0, spent: 0, notes: "" }); setOpen(true); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Add channel</Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <StatCard label="Total Planned" value={fmtMoney(planned)} icon={Wallet} accent />
        <StatCard label="Total Spent" value={fmtMoney(spent)} icon={CurrencyDollar} />
        <StatCard label="Remaining" value={fmtMoney(planned - spent)} icon={ChartPieSlice} />
      </div>

      {rows.length === 0 ? (
        <div className="card-matte p-10 text-center" data-testid="budget-empty-state">
          <Wallet size={36} className="mx-auto text-[#5a5a5a] mb-3" />
          <div className="font-head font-semibold text-lg">No ad budget planned yet</div>
          <p className="text-sm text-[#9a9a9a] mt-1 max-w-md mx-auto">Add channels to plan your spend manually — or connect Meta Ads / Google Ads to view real ad spend and campaign performance.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button data-testid="budget-empty-add-btn" onClick={() => { setCur({ channel: "", planned: 0, spent: 0, notes: "" }); setOpen(true); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Add channel</Button>
            <Button data-testid="budget-empty-connect-btn" variant="ghost" onClick={() => navigate("/ads")} className="border border-white/15 text-white hover:bg-white/5">Connect Meta / Google Ads</Button>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          {rows.map((r) => {
            const pct = r.planned ? Math.min(100, Math.round((r.spent / r.planned) * 100)) : 0;
            return (
              <div key={r.id} className="card-matte p-4" data-testid="budget-card">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{r.channel}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setCur({ ...r }); setOpen(true); }} className="text-[#9a9a9a] hover:text-white p-1.5"><PencilSimple size={15} /></button>
                    <button onClick={() => delM.mutate(r.id)} className="text-[#9a9a9a] hover:text-red-400 p-1.5"><Trash size={15} /></button>
                  </div>
                </div>
                <div className="flex justify-between text-sm text-[#9a9a9a] mt-1 mb-2">
                  <span>{fmtMoney(r.spent)} spent</span><span>of {fmtMoney(r.planned)}</span>
                </div>
                <Progress value={pct} className="h-2 bg-white/10 [&>div]:bg-[#6B3DFF]" />
                {r.notes && <div className="text-[11px] text-[#7a7a7a] mt-2">{r.notes}</div>}
              </div>
            );
          })}
        </div>

        <div className="card-matte p-5">
          <div className="label-caps mb-4">Planned vs Spent</div>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={rows} margin={{ left: -10 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="channel" stroke="#5a5a5a" tick={{ fontSize: 9, fill: "#9a9a9a" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#10101A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10 }} formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="planned" name="Planned" fill="#3a3a3a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="spent" name="Spent" fill="#6B3DFF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">{cur?.id ? "Edit" : "Add"} channel</DialogTitle></DialogHeader>
          {cur && (
            <div className="space-y-3">
              <div><Label className="label-caps">Channel</Label><Input value={cur.channel} onChange={(e) => setCur({ ...cur, channel: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" data-testid="budget-channel" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Planned ($)</Label><Input type="number" value={cur.planned} onChange={(e) => setCur({ ...cur, planned: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Spent ($)</Label><Input type="number" value={cur.spent} onChange={(e) => setCur({ ...cur, spent: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
              </div>
              <div><Label className="label-caps">Notes</Label><Input value={cur.notes} onChange={(e) => setCur({ ...cur, notes: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-budget-btn" onClick={() => { saveM.mutate(cur); setOpen(false); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
